import { Expression, ExpressionObject, Token } from '@benzinga/session';
import { TokenizedExpression } from '../SearchBar';
import { SearchItem } from '@benzinga/search-modules';

export interface TokenData<E> {
  leftOperand: E;
  operator: ReturnType<typeof getExpressionType>;
  rightOperand: Token<string>[2];
}

const getExpressionType = (operator: Token<string>[1]): 'array' | 'object' | Omit<Token<string>[1], 'all' | 'any'> => {
  switch (operator) {
    case 'all':
    case 'any':
      return 'array';
    default:
      return operator;
  }
};

export const parseExpression = <E extends string>(
  expression: Expression<E>,
  genModuleItem: (item: TokenData<E>) => SearchItem | undefined,
  displayOr?: boolean,
): TokenizedExpression[] => {
  let idIndex = 0;
  let groupIndex = 0;
  const parse = (token: ExpressionObject<E>): TokenizedExpression[] => {
    idIndex += 1;
    const index = idIndex;
    switch (token.operator) {
      case 'group': {
        groupIndex += 1;
        const index = groupIndex;
        return [
          { groupIndex: index, id: `group_start_${index}`, operator: 'group_start' },
          ...parse(token.group),
          { groupIndex: index, id: `group_end_${index}`, operator: 'group_end' },
        ];
      }
      case 'not': {
        groupIndex += 1;
        const index = groupIndex;
        return [
          { groupIndex: index, id: `not_start_${index}`, operator: 'not_start' },
          ...parse(token.group),
          { groupIndex: index, id: `not_end_${index}`, operator: 'not_end' },
        ];
      }
      case 'or':
        if (displayOr) {
          return [...parse(token.lhs), { id: `or_${index}`, operator: 'or' }, ...parse(token.rhs)];
        } else {
          return [...parse(token.lhs), ...parse(token.rhs)];
        }
      case 'and':
        return [...parse(token.lhs), { id: `and_${index}`, operator: 'and' }, ...parse(token.rhs)];

      case 'expression':
        switch (token.expression[1]) {
          case 'all':
          case 'any': {
            if (Array.isArray(token.expression[2])) {
              return (token.expression[2] as string[])
                .flatMap(value => {
                  const data = genModuleItem({
                    leftOperand: token.expression[0],
                    operator: 'array',
                    rightOperand: value,
                  });
                  if (data == null) return [];
                  return [{ ...data, operator: 'expression' as const }];
                })
                .flatMap<TokenizedExpression>((value, index, array) =>
                  array.length - 1 !== index // check for the last item
                    ? token.expression[1] === 'all'
                      ? [value, { id: `and_${value.id}`, operator: 'and' }]
                      : displayOr
                        ? [value, { id: `or_${value.id}`, operator: 'or' }]
                        : [value]
                    : [value],
                );
            } else {
              const data = genModuleItem({
                leftOperand: token.expression[0],
                operator: 'object',
                rightOperand: token.expression[2],
              });
              if (data == null) return [];
              return [
                {
                  ...data,
                  operator: 'expression' as const,
                },
              ];
            }
          }
          default: {
            const data = genModuleItem({
              leftOperand: token.expression[0],
              operator: getExpressionType(token.expression[1]),
              rightOperand: token.expression[2] as string,
            });
            if (data == null) return [];
            return [
              {
                ...data,
                operator: 'expression',
              },
            ];
          }
        }
      default:
        return [];
    }
  };

  const expressionObject = expression?.getExpressionType();
  return parse(expressionObject);
};

export type CleanTokenizedExpression<T> = Exclude<TokenizedExpression<T>, { operator: 'position' }>;

const cleanUpTokens = <T>(tokens: TokenizedExpression<T>[]): CleanTokenizedExpression<T>[] =>
  tokens
    .filter((o): o is CleanTokenizedExpression<T> => o.operator !== 'position')
    .reduce<CleanTokenizedExpression<T>[]>((acc, token, index, tokens) => {
      if (acc.length === 0) {
        switch (token.operator) {
          case 'and':
          case 'or':
            return acc;
          default:
            acc.push(token);
            return acc;
        }
      } else if (index === tokens.length - 1) {
        switch (token.operator) {
          case 'and':
          case 'or':
            return acc;
          default:
            break;
        }
      }

      switch (token.operator) {
        case 'group_start':
        case 'not_start':
        case 'expression':
          switch (acc[acc.length - 1].operator) {
            case 'and':
            case 'or':
            case 'group_start':
            case 'not_start':
              acc.push(token);
              return acc;
            default:
              acc.push({ operator: 'or' }, token);
              return acc;
          }
        case 'group_end':
        case 'not_end':
          switch (acc[acc.length - 1].operator) {
            case 'and':
            case 'or':
              acc.pop();
              acc.push(token);
              return acc;
            default:
              acc.push(token);
              return acc;
          }
        case 'or':
        case 'and':
          switch (acc[acc.length - 1].operator) {
            case 'and':
            case 'or':
              acc.pop();
              acc.push(token);
              return acc;
            default:
              acc.push(token);
              return acc;
          }
      }
      return acc;
    }, []);

type NestedTokenizedExpression<T> =
  | Exclude<CleanTokenizedExpression<T>, { operator: 'group_start' | 'group_end' | 'not_start' | 'not_end' }>
  | {
      operator: 'group' | 'not';
      group: NestedTokenizedExpression<T>[];
    };

const groupTokens = <T>(tokens: CleanTokenizedExpression<T>[]): NestedTokenizedExpression<T>[] => {
  const root: NestedTokenizedExpression<T>[] = [];
  const stack = [root];
  let current = root;

  tokens.forEach(token => {
    if (token.operator === 'group_start' || token.operator === 'not_start') {
      current.push({ group: [], operator: token.operator === 'not_start' ? 'not' : 'group' });
      stack.push((current[current.length - 1] as { group: []; operator: 'not' | 'group' }).group);
      current = stack[stack.length - 1];
    } else if (token.operator === 'group_end' || token.operator === 'not_end') {
      stack.pop();
      current = stack[stack.length - 1];
    } else {
      current.push(token);
    }
  });

  return root;
};

type ExpressionTokenType<E> =
  | { operator: 'expression'; expression: TokenData<E> }
  | { lhs: ExpressionTokenType<E>; operator: 'or' | 'and'; rhs: ExpressionTokenType<E> }
  | {
      group: ExpressionTokenType<E>;
      operator: 'not' | 'group';
    }
  | { group: ExpressionTokenType<E>; operator: 'init' }
  | { operator: 'none' };

const insertExpression = <E>(
  token: ExpressionTokenType<E>,
  acc: {
    parent: ExpressionTokenType<E>;
    rootExpression: { group: ExpressionTokenType<E>; operator: 'init' | 'group' | 'not' };
  },
) => {
  switch (token.operator) {
    case 'group':
    case 'not':
      switch (acc.parent.operator) {
        case 'init':
        case 'group':
        case 'not':
          acc.parent.group = token;
          break;
        case 'or':
        case 'and':
          acc.parent.rhs = token;
          break;
        case 'expression':
          console.error('we should not get here');
          break;
      }
      break;
    case 'or':
    case 'and':
      switch (acc.parent.operator) {
        case 'init':
          token.lhs = acc.parent.group;
          acc.parent.group = token;
          break;
        case 'group':
        case 'not':
          token.lhs = acc.parent;
          acc.parent.group = token;
          break;
        case 'or':
        case 'and':
          token.lhs = acc.parent;
          break;
        case 'expression':
          token.lhs = acc.parent;
          acc.parent = token;
          break;
      }
      acc.rootExpression.group = token;
      acc.parent = acc.rootExpression.group;
      break;
    case 'expression':
      switch (acc.parent.operator) {
        case 'init':
        case 'group':
        case 'not':
          acc.parent.group = token;
          break;
        case 'or':
        case 'and':
          acc.parent.rhs = token;
          break;
        case 'expression':
          console.error('we should not get here');
          break;
      }
      break;
  }
};

const toExpressionType = <E extends G, T extends TokenData<E>, G = T extends TokenData<infer G> ? G : never>(
  tokens: NestedTokenizedExpression<T>[],
): ExpressionTokenType<E> => {
  const rootExpression: ExpressionTokenType<E> = { group: { operator: 'none' }, operator: 'init' };

  const tokenTree = tokens.reduce<{
    rootExpression: Extract<ExpressionTokenType<E>, { operator: 'group' | 'init' | 'not' }>;
    parent: ExpressionTokenType<E>;
  }>(
    (acc, token) => {
      switch (token.operator) {
        case 'expression':
          insertExpression(
            {
              expression: token.data,
              operator: 'expression',
            },
            acc,
          );
          return acc;
        case 'or':
        case 'and':
          insertExpression(
            {
              lhs: { operator: 'none' },
              operator: token.operator ?? 'or',
              rhs: { operator: 'none' },
            },
            acc,
          );
          return acc;
        case 'group':
        case 'not':
          insertExpression(
            {
              group: toExpressionType(token.group),
              operator: token.operator,
            },
            acc,
          );
          return acc;
      }
      return acc;
    },
    {
      parent: rootExpression,
      rootExpression: rootExpression,
    },
  );

  return tokenTree.rootExpression.group as ExpressionTokenType<E>;
};

type CompressedToken<E> =
  | { lhs: CompressedToken<E>; operator: 'or' | 'and'; rhs: CompressedToken<E> }
  | {
      group: CompressedToken<E>;
      operator: 'not' | 'group';
    }
  | { group: CompressedToken<E>; operator: 'init' }
  | { operator: 'none' }
  | {
      operator: 'expression';
      expression: {
        leftOperand: E;
        operator: Token<string>[1] | 'array' | 'object';
        rightOperand: Token<string>[2];
      };
    };

const compressedTokens = <T extends TokenData<E>, E = T extends TokenData<infer G> ? G : never>(
  token: ExpressionTokenType<E>,
  getExpressionType: (expressionType: T) => 'array' | 'object' | 'match' | 'phrase' | 'search',
): CompressedToken<E> => {
  const convertExpression = (
    token: Extract<CompressedToken<E>, { operator: 'expression' }>,
    operator: 'or' | 'and' = 'or',
  ): Extract<CompressedToken<E>, { operator: 'expression' }> => {
    if (token.expression.operator === 'array') {
      return {
        expression: {
          ...token.expression,
          operator: operator === 'or' ? 'any' : 'all',
          rightOperand: [token.expression.rightOperand],
        },
        operator: 'expression',
      };
    } else if (token.expression.operator === 'object') {
      return {
        expression: {
          ...token.expression,
          operator: 'any',
          rightOperand: token.expression.rightOperand,
        },
        operator: 'expression',
      };
    } else {
      return token;
    }
  };

  switch (token.operator) {
    case 'expression':
      return token as CompressedToken<E>;
    case 'or':
    case 'and': {
      const lhsToken = compressedTokens(token.lhs, getExpressionType);
      const rhsToken = compressedTokens(token.rhs, getExpressionType);
      if (lhsToken.operator === 'expression' && rhsToken.operator === 'expression') {
        if (lhsToken.expression.leftOperand === rhsToken.expression.leftOperand) {
          if (
            getExpressionType(lhsToken.expression as T) === 'array' &&
            getExpressionType(rhsToken.expression as T) === 'array'
          ) {
            if (lhsToken.expression.operator === 'array' && rhsToken.expression.operator === 'array') {
              return {
                expression: {
                  ...rhsToken.expression,
                  operator: token.operator === 'or' ? 'any' : 'all',
                  rightOperand: [
                    lhsToken.expression.rightOperand as unknown,
                    rhsToken.expression.rightOperand as unknown,
                  ],
                },
                operator: 'expression',
              };
            } else if (
              (lhsToken.expression.operator === 'any' && rhsToken.expression.operator === 'any') ||
              (lhsToken.expression.operator === 'all' && rhsToken.expression.operator === 'all')
            ) {
              return {
                expression: {
                  ...rhsToken.expression,
                  operator: lhsToken.expression.operator,
                  rightOperand: [
                    ...(lhsToken.expression.rightOperand as unknown[]),
                    ...(rhsToken.expression.rightOperand as unknown[]),
                  ],
                },
                operator: 'expression',
              };
            } else if (
              (lhsToken.expression.operator === 'array' && rhsToken.expression.operator === 'any') ||
              (lhsToken.expression.operator === 'array' && rhsToken.expression.operator === 'all')
            ) {
              return {
                expression: {
                  ...rhsToken.expression,
                  rightOperand: [
                    lhsToken.expression.rightOperand as unknown,
                    ...(rhsToken.expression.rightOperand as unknown[]),
                  ],
                },
                operator: 'expression',
              };
            } else if (
              (lhsToken.expression.operator === 'any' && rhsToken.expression.operator === 'array') ||
              (lhsToken.expression.operator === 'all' && rhsToken.expression.operator === 'array')
            ) {
              return {
                expression: {
                  ...lhsToken.expression,
                  rightOperand: [
                    ...(lhsToken.expression.rightOperand as unknown[]),
                    rhsToken.expression.rightOperand as unknown,
                  ],
                },
                operator: 'expression',
              };
            }
          }
        }
        return {
          lhs: convertExpression(lhsToken, token.operator),
          operator: token.operator,
          rhs: convertExpression(rhsToken, token.operator),
        };
      }

      if (lhsToken.operator === 'expression') {
        return { lhs: convertExpression(lhsToken, token.operator), operator: token.operator, rhs: rhsToken };
      } else if (rhsToken.operator === 'expression') {
        return { lhs: lhsToken, operator: token.operator, rhs: convertExpression(rhsToken, token.operator) };
      } else {
        return { lhs: lhsToken, operator: token.operator, rhs: rhsToken };
      }
    }
    case 'group':
    case 'not': {
      const group = compressedTokens(token.group, getExpressionType);
      if (group.operator === 'expression') {
        return { group: convertExpression(group), operator: token.operator };
      } else {
        return { group, operator: token.operator };
      }
    }
  }
  return token as CompressedToken<E>;
};

export const toExpressionToken = <T extends TokenData<E>, E = T extends TokenData<infer G> ? G : never>(
  token: CompressedToken<E>,
): ExpressionObject<E> => {
  switch (token.operator) {
    case 'expression':
      switch (token.expression.operator) {
        case 'all':
        case 'any':
          return {
            expression: [token.expression.leftOperand, token.expression.operator, token.expression.rightOperand],
            operator: 'expression',
          };
        case 'match':
        case 'phrase':
        case 'search':
          return {
            expression: [
              token.expression.leftOperand,
              token.expression.operator,
              token.expression.rightOperand as string,
            ],
            operator: 'expression',
          };
        case 'object':
        case 'array':
          return { operator: 'none' };
        default:
          return token as unknown as ExpressionObject<E>;
      }
    case 'and':
    case 'or':
      return { lhs: toExpressionToken(token.lhs), operator: token.operator, rhs: toExpressionToken(token.rhs) };
    case 'init':
      return { group: toExpressionToken(token.group), operator: 'group' };
    case 'group':
    case 'not':
      return { group: toExpressionToken(token.group), operator: token.operator };
    default:
      return token;
  }
};

export const toExpression = <T extends TokenData<E>, E = T extends TokenData<infer G> ? G : never>(
  tokens: TokenizedExpression<T>[],
  getExpressionType: (expressionType: T) => 'array' | 'object' | 'match' | 'phrase' | 'search',
): Expression<E> => {
  const ex = toExpressionToken<T, E>(
    (
      compressedTokens<T, E>(
        {
          group: toExpressionType(groupTokens(cleanUpTokens(tokens))),
          operator: 'group',
        },
        getExpressionType,
      ) as Extract<CompressedToken<E>, { operator: 'group' | 'not' }>
    ).group,
  );
  if (ex.operator !== 'none') {
    return Expression.fromExpressionType<E>(ex);
  } else {
    return Expression.new();
  }
};
